import {
  AfterViewInit,
  ApplicationRef,
  Component,
  ComponentRef,
  ElementRef,
  EventEmitter,
  Injector,
  OnDestroy,
  Type,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { gsap } from 'gsap';
import { Observable, Subject } from 'rxjs';

@Component({
  selector: 'dialog-container',
  templateUrl: './dialog-container.component.html',
})
export class DialogContainerComponent implements OnDestroy, AfterViewInit {

  @ViewChild('dialogComponent', {read: ViewContainerRef, static: true}) public componentContainer: ViewContainerRef;
  @ViewChild('modalElement') public modalElement: ElementRef;

  public childComponent: ComponentRef<any> = null;
  public data: any = null;
  public classes: string = 'default medium';
  public closeButtonEnabled: boolean = true;

  public visible: boolean = false;
  public locked: boolean = false;

  public closeDialogSubject: Subject<boolean> = new Subject<boolean>();
  public confirmDialogSubject: Subject<boolean> = new Subject<boolean>();
  public cancelDialogSubject: Subject<boolean> = new Subject<boolean>();

  /**
   * Constructor
   *
   * @param {ApplicationRef} appRef
   * @param {Injector} injector
   */
  public constructor(
    private appRef: ApplicationRef,
    private injector: Injector,
  ) {
  }

  /**
   * When the view is initialized start animation
   *
   * @return void
   */
  public ngAfterViewInit(): void {
    gsap.fromTo(
      this.modalElement.nativeElement,
      {
        autoAlpha: 0,
        translateY: '0%'
      },
      {
        autoAlpha: 1,
        duration: 1,
      });
  }

  /**
   * Generate the dialog body with the component
   *
   * @param {Type<any>} component
   * @param {Object} data
   * @param {string} classes
   *
   * @return void
   */
  public generate(component: Type<any>, data: any, classes: string = 'default medium'): void {

    this.childComponent = this.componentContainer.createComponent(component);
    this.childComponent.instance.data = data;

    this.classes = classes;

    this.componentContainer.insert(this.childComponent.hostView);

    if (data.closeButtonEnabled === true || data.closeButtonEnabled === false) {
      this.closeButtonEnabled = data.closeButtonEnabled;
    }

    this.setListeners();
  }

  /**
   * User clicks to close the dialog
   *
   * @return void
   */
  public onClose(): void {
    this.close();
  }

  /**
   * Close the dialog
   *
   * @return void
   */
  public close(): void {
    if (!this.locked) {
      this.locked = true;
      gsap.fromTo(this.modalElement.nativeElement, {autoAlpha: 1, translateY: '0%'}, {
        autoAlpha: 0,
        duration: 1,
        onComplete: () => {
          this.visible = false;
          this.locked = false;
          this.closeDialogSubject.next(true);
        },
      });
    }
  }

  /**
   * Listen to when the dialog is closed
   *
   * @return {Observable<boolean>}
   */
  public dialogClosed(): Observable<boolean> {
    return this.closeDialogSubject.asObservable();
  }

  /**
   * Listen to when the dialog is confirmed
   *
   * @return {Observable<boolean>}
   */
  public dialogConfirmed(): Observable<boolean> {
    return this.confirmDialogSubject.asObservable();
  }

  /**
   * When the Component is destroyed
   */
  public ngOnDestroy(): void {
    this.childComponent.destroy();
  }

  /**
   * Set the listeners of the child Component
   *
   * @return void
   */
  private setListeners(): void {
    if (this.childComponent.instance.closed instanceof EventEmitter) {
      this.childComponent.instance.closed.subscribe(
        () => {
          this.close();
        },
      );
    }

    if (this.childComponent.instance.canceled instanceof EventEmitter) {
      this.childComponent.instance.canceled.subscribe(
        () => {
          this.cancelDialogSubject.next(true);
          this.close();
        },
      );
    }

    if (this.childComponent.instance.confirmed instanceof EventEmitter) {
      this.childComponent.instance.confirmed.subscribe(
        () => {

          this.confirmDialogSubject.next(true);
          this.close();
        },
      );
    }
  }
}
